// Aufgeklappter Inhalt einer Objektmappe (Risiken-Reiter → Objektmappen).
//
// Fachvorgabe: unter der Objektzeile stehen die detaillierten Risikoinfos und
// darunter drei Listen — Verträge, Schäden, Vorgänge (Risiko UND Vertrag). Aus
// jeder Zeile kommt man in das jeweilige Objekt (Risiko / Vertrag / Schaden /
// Vorgang).
//
// Geladen wird erst beim Aufklappen und dann pro Risiko genau einmal
// (Cache im Elternteil wäre Zustandsdopplung — die Komponente wird beim
// Zuklappen ausgehängt und der Request ist billig).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { apiFetch } from "../../utils/apiFetch";
import { BRAND } from "../risikoanalyseStep/tokens";
import { gefahrenAus, type VertragCharLike } from "./vertragsGefahren";
import { resolvePeople } from "../vorgang/shared/people";
import { listAppUsers, type AppUser } from "../../services/userService";

/* ── Typen (schlank gehalten: nur was die Mappe anzeigt) ─────────────────── */

interface CharacteristicLike {
  nameBezeichnung?: string | null;
  riskCharacteristicValue?: string | null;
}

interface MappeContract {
  id: number;
  contractNbr?: string | null;
  branch?: string | null;
  beginPolicyTerm?: string | null;
  endPolicyTerm?: string | null;
  endOfContract?: string | null;
  premiumAnnualGross?: number | string | null;
  paymentFrequency?: number | null;
  gesellschaft?: { id?: number; name?: string | null } | null;
  insurer?: { id?: number; lastname?: string | null; displayName?: string | null } | null;
  characteristics?: VertragCharLike[] | null;
}

interface MappeDamage {
  id: number;
  damageNbr?: string | null;
  externalDamageNbr?: string | null;
  damageType?: string | null;
  damageDate?: string | null;
  status?: string | null;
  title?: string | null;
}

interface MappeVorgang {
  id: number;
  titel?: string | null;
  status?: string | null;
  statusManuell?: string | null;
  frist?: string | null;
  twv?: string | null;
  bearbeiter?: unknown[] | null;
  hauptverantwortlich?: string[] | null;
}

/* ── Formatierung ───────────────────────────────────────────────────────── */

const eur = (v: number) => `${Math.round(v).toLocaleString("de-DE")} €`;

const num = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
};

const datum = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("de-DE");
};

/** Contract.paymentFrequency = Zahlungen pro Jahr (siehe ZAHLWEISE_TO_FREQUENCY). */
const ZAHLWEISE: Record<number, string> = {
  1: "jährlich",
  2: "halbjährlich",
  4: "vierteljährlich",
  12: "monatlich",
};
const zahlweise = (f?: number | null): string =>
  f == null ? "—" : ZAHLWEISE[f] ?? `${f}× pro Jahr`;

const versicherer = (c: MappeContract): string =>
  c.gesellschaft?.name ||
  c.insurer?.displayName ||
  c.insurer?.lastname ||
  "—";

/**
 * Versicherte Gefahren samt Selbstbehalt eines Vertrags — gemeinsame
 * Ableitung mit der Immobilienmappe (`vertragsGefahren.ts`).
 *
 * ⚠️ Hier stand bis 25.08.2026 eine eigene Fassung, die den Wert unter
 * `riskCharacteristicValue` suchte. Vertragsmerkmale tragen ihn aber unter
 * `contractCharacteristicValue` (`ContractCharacteristics`) — die Spalten
 * „Gefahren" und „SB" blieben deshalb IMMER leer.
 */

/* ── Styles ─────────────────────────────────────────────────────────────── */

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
    paddingTop: "12px",
    paddingBottom: "14px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: "#fbf9fb",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  block: { display: "flex", flexDirection: "column", rowGap: "6px" },
  blockHead: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "8px",
  },
  blockTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.82rem",
    fontWeight: 700,
    color: BRAND.ink,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  blockCount: { fontSize: "0.76rem", color: BRAND.muted },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    columnGap: "14px",
    rowGap: "4px",
  },
  infoKey: { fontSize: "0.72rem", color: BRAND.muted, fontWeight: 600 },
  infoVal: { fontSize: "0.84rem", color: BRAND.ink },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  table: { borderCollapse: "collapse", width: "100%", fontSize: "0.8rem" },
  th: {
    fontSize: "0.64rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    textAlign: "left",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "9px",
    paddingRight: "9px",
    whiteSpace: "nowrap",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: BRAND.border,
  },
  thRight: { textAlign: "right" },
  td: {
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "9px",
    paddingRight: "9px",
    color: BRAND.ink,
    verticalAlign: "top",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: BRAND.borderDashed,
  },
  tdRight: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  tdMuted: { color: BRAND.muted },
  linkRow: { cursor: "pointer", ":hover": { backgroundColor: "#f7f1f6" } },
  linkNr: {
    color: BRAND.purple,
    fontWeight: 700,
    textDecorationLine: "underline",
    textDecorationColor: "#e6d3e3",
    textUnderlineOffset: "3px",
    whiteSpace: "nowrap",
  },
  gefahrList: { display: "flex", flexDirection: "column", rowGap: "2px", minWidth: "200px" },
  gefahrItem: { fontSize: "0.76rem", color: BRAND.ink, whiteSpace: "normal" },
  leer: { fontSize: "0.78rem", color: BRAND.muted, fontStyle: "italic" },
  ghostBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    fontWeight: 700,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    width: "fit-content",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    ":hover": { backgroundColor: "#f7f1f6" },
  },
});

/* ── Komponente ─────────────────────────────────────────────────────────── */

export interface MappeDetailsProps {
  riskId: number;
  /** Merkmale des Risikoobjekts — liegen aus der Liste schon vor. */
  characteristics?: CharacteristicLike[] | null;
  /** Versicherungssumme des Objekts (Fallback für die Vertragszeile). */
  versicherungssumme?: number | null;
}

export function MappeDetails({ riskId, characteristics, versicherungssumme }: MappeDetailsProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  // Bearbeiter/Hauptverantwortliche sind User-Ids — die Namensauflösung braucht
  // die User-Liste (modulweit gecacht, siehe listAppUsers).
  const [users, setUsers] = useState<AppUser[]>([]);
  useEffect(() => {
    let cancelled = false;
    listAppUsers()
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch(() => {
        /* Namen bleiben dann die Rohschlüssel — kein Grund, die Mappe zu leeren */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [contracts, setContracts] = useState<MappeContract[]>([]);
  const [damages, setDamages] = useState<MappeDamage[]>([]);
  const [vorgaenge, setVorgaenge] = useState<MappeVorgang[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiFetch<MappeContract[]>(`contracts/by-risk/${riskId}`)
        .then(({ data }) => (Array.isArray(data) ? data : []))
        .catch(() => [] as MappeContract[]),
      apiFetch<MappeDamage[]>(`damages/by-risk/${riskId}`)
        .then(({ data }) => (Array.isArray(data) ? data : []))
        .catch(() => [] as MappeDamage[]),
      apiFetch<MappeVorgang[]>(`vorgaenge/by-risk/${riskId}`)
        .then(({ data }) => (Array.isArray(data) ? data : []))
        .catch(() => [] as MappeVorgang[]),
    ])
      .then(([c, d, v]) => {
        if (cancelled) return;
        setContracts(c);
        setDamages(d);
        setVorgaenge(v);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [riskId]);

  /** Detaillierte Risikoinfos = alle gefüllten Merkmale des Objekts. */
  const infos = useMemo(
    () =>
      (characteristics ?? [])
        .filter(
          (c) =>
            (c.nameBezeichnung ?? "").trim() !== "" &&
            String(c.riskCharacteristicValue ?? "").trim() !== "",
        )
        .map((c) => ({
          key: (c.nameBezeichnung ?? "").trim(),
          value: String(c.riskCharacteristicValue).trim(),
        })),
    [characteristics],
  );

  const personen = (v: MappeVorgang, feld: "bearbeiter" | "hauptverantwortlich"): string => {
    const raw = feld === "bearbeiter" ? v.bearbeiter : v.hauptverantwortlich;
    const people = resolvePeople((raw as unknown[]) ?? [], users);
    return people.length ? people.map((p) => p.name).join(", ") : "—";
  };

  return (
    <div className={styles.root}>
      {/* Detaillierte Risikoinfos */}
      <div className={styles.block}>
        <div className={styles.blockHead}>
          <span className={styles.blockTitle}>Risikoinfos</span>
          <span className={styles.blockCount}>{infos.length} Angaben</span>
        </div>
        {infos.length === 0 ? (
          <div className={styles.leer}>Zu diesem Objekt sind keine Merkmale erfasst.</div>
        ) : (
          <div className={styles.infoGrid}>
            {infos.map((i) => (
              <div key={i.key}>
                <div className={styles.infoKey}>{i.key}</div>
                <div className={styles.infoVal}>{i.value}</div>
              </div>
            ))}
          </div>
        )}
        <button type="button" className={styles.ghostBtn} onClick={() => navigate(`/risks/${riskId}`)}>
          Risiko öffnen →
        </button>
      </div>

      {loading ? (
        <div className={styles.leer}>Verträge, Schäden und Vorgänge werden geladen …</div>
      ) : (
        <>
          {/* Verträge */}
          <div className={styles.block}>
            <div className={styles.blockHead}>
              <span className={styles.blockTitle}>Verträge</span>
              <span className={styles.blockCount}>{contracts.length}</span>
            </div>
            {contracts.length === 0 ? (
              <div className={styles.leer}>Kein Vertrag zu diesem Objekt.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Vertragsnummer</th>
                      <th className={styles.th}>Versicherer</th>
                      <th className={styles.th}>Beginn</th>
                      <th className={styles.th}>Ablauf</th>
                      <th className={mergeClasses(styles.th, styles.thRight)}>Jahresprämie</th>
                      <th className={styles.th}>Zahlweise</th>
                      <th className={mergeClasses(styles.th, styles.thRight)}>Vers.-Summe</th>
                      <th className={styles.th}>Gefahren</th>
                      <th className={styles.th}>SB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((c) => {
                      const gefahren = gefahrenAus(c.characteristics);
                      const praemie = num(c.premiumAnnualGross);
                      // Versicherungssumme ist im Datenmodell am Risikoobjekt
                      // gepflegt, nicht am Vertrag — deshalb die des Objekts.
                      const vsGefahren = gefahren.reduce((acc, g) => acc + (g.vs ?? 0), 0);
                      const vs = vsGefahren > 0 ? vsGefahren : versicherungssumme ?? null;
                      return (
                        <tr
                          key={c.id}
                          className={styles.linkRow}
                          onClick={() => navigate(`/contracts/${c.id}`)}
                        >
                          <td className={styles.td}>
                            <span className={styles.linkNr}>{c.contractNbr || `#${c.id}`}</span>
                            {c.branch && (
                              <div className={mergeClasses(styles.infoKey)}>{c.branch}</div>
                            )}
                          </td>
                          <td className={styles.td}>{versicherer(c)}</td>
                          <td className={styles.td}>{datum(c.beginPolicyTerm)}</td>
                          <td className={styles.td}>
                            {datum(c.endPolicyTerm ?? c.endOfContract)}
                          </td>
                          <td className={mergeClasses(styles.td, styles.tdRight)}>
                            {praemie != null ? eur(praemie) : "—"}
                          </td>
                          <td className={styles.td}>{zahlweise(c.paymentFrequency)}</td>
                          <td className={mergeClasses(styles.td, styles.tdRight)}>
                            {vs != null && vs > 0 ? eur(vs) : "—"}
                          </td>
                          <td className={styles.td}>
                            {gefahren.length === 0 ? (
                              <span className={styles.tdMuted}>—</span>
                            ) : (
                              <div className={styles.gefahrList}>
                                {gefahren.map((g) => (
                                  <span key={g.id} className={styles.gefahrItem}>
                                    {g.label}
                                    {g.vs != null ? ` · ${eur(g.vs)}` : ""}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className={styles.td}>
                            {gefahren.some((g) => g.sb) ? (
                              <div className={styles.gefahrList}>
                                {gefahren
                                  .filter((g) => g.sb)
                                  .map((g) => (
                                    <span key={g.id} className={styles.gefahrItem}>
                                      {g.label}: {g.sb}
                                    </span>
                                  ))}
                              </div>
                            ) : (
                              <span className={styles.tdMuted}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Schäden — nach Schadentag absteigend (Sortierung kommt vom Backend) */}
          <div className={styles.block}>
            <div className={styles.blockHead}>
              <span className={styles.blockTitle}>Schäden</span>
              <span className={styles.blockCount}>{damages.length}</span>
            </div>
            {damages.length === 0 ? (
              <div className={styles.leer}>Kein Schaden zu diesem Objekt.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Schadennr. Courté</th>
                      <th className={styles.th}>Schadennr. VR</th>
                      <th className={styles.th}>Schadenart</th>
                      <th className={styles.th}>Schadentag</th>
                      <th className={styles.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {damages.map((d) => (
                      <tr
                        key={d.id}
                        className={styles.linkRow}
                        onClick={() => navigate(`/schaeden/${d.id}`)}
                      >
                        <td className={styles.td}>
                          <span className={styles.linkNr}>{d.damageNbr || `#${d.id}`}</span>
                          {d.title && <div className={styles.infoKey}>{d.title}</div>}
                        </td>
                        <td className={mergeClasses(styles.td, styles.tdMuted)}>
                          {d.externalDamageNbr || "—"}
                        </td>
                        <td className={styles.td}>{d.damageType || "—"}</td>
                        <td className={styles.td}>{datum(d.damageDate)}</td>
                        <td className={styles.td}>{d.status || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Vorgänge — Zuordnung am Risiko ODER an einem seiner Verträge */}
          <div className={styles.block}>
            <div className={styles.blockHead}>
              <span className={styles.blockTitle}>Vorgänge</span>
              <span className={styles.blockCount}>{vorgaenge.length} · Risiko und Vertrag</span>
            </div>
            {vorgaenge.length === 0 ? (
              <div className={styles.leer}>Kein Vorgang zu diesem Objekt oder seinen Verträgen.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Titel</th>
                      <th className={styles.th}>Status</th>
                      <th className={styles.th}>Hauptv.</th>
                      <th className={styles.th}>Bearbeiter</th>
                      <th className={styles.th}>Frist</th>
                      <th className={styles.th}>Wiedervorlage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vorgaenge.map((v) => (
                      <tr
                        key={v.id}
                        className={styles.linkRow}
                        onClick={() => navigate(`/vorgaenge/${v.id}`)}
                      >
                        <td className={styles.td}>
                          <span className={styles.linkNr}>{v.titel || `Vorgang #${v.id}`}</span>
                        </td>
                        <td className={styles.td}>{v.status || v.statusManuell || "—"}</td>
                        <td className={mergeClasses(styles.td, styles.tdMuted)}>
                          {personen(v, "hauptverantwortlich")}
                        </td>
                        <td className={styles.td}>{personen(v, "bearbeiter")}</td>
                        <td className={styles.td}>{datum(v.frist)}</td>
                        <td className={styles.td}>{datum(v.twv)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
